import React, { useState, useEffect } from 'react';
import { Table, Card, Button, message, Space, Input, Tag } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../../config';

const PeriodList = () => {
    const [periods, setPeriods] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const navigate = useNavigate();

    // Fetch periods data
    const fetchPeriods = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${config.apiUrl}/api/v1/periods`);
            setPeriods(response.data.data || []);
        } catch (error) {
            message.error('Không thể tải danh sách đợt tuyển sinh!');
            console.error('Error fetching periods:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPeriods();
    }, []);

    const getStatusColor = (status) => {
        switch (status) {
            case 'active':
                return 'green';
            case 'upcoming':
                return 'blue';
            case 'closed':
                return 'red';
            default:
                return 'default';
        }
    };

    // Table columns definition
    const columns = [
        {
            title: 'Tên đợt tuyển sinh',
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name),
            filteredValue: [searchText],
            onFilter: (value, record) => {
                return (
                    String(record.name).toLowerCase().includes(value.toLowerCase()) ||
                    String(record.description).toLowerCase().includes(value.toLowerCase())
                );
            },
        },
        {
            title: 'Ngày bắt đầu',
            dataIndex: 'start_date',
            key: 'start_date',
            render: (date) => date ? new Date(date).toLocaleString('vi-VN') : '-',
            sorter: (a, b) => new Date(a.start_date) - new Date(b.start_date),
        },
        {
            title: 'Ngày kết thúc',
            dataIndex: 'end_date',
            key: 'end_date',
            render: (date) => date ? new Date(date).toLocaleString('vi-VN') : '-',
            sorter: (a, b) => new Date(a.end_date) - new Date(b.end_date),
        },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {status === 'active' ? 'Đang diễn ra' :
                     status === 'upcoming' ? 'Sắp diễn ra' :
                     status === 'closed' ? 'Đã kết thúc' : 'Không xác định'}
                </Tag>
            ),
            filters: [
                { text: 'Đang diễn ra', value: 'active' },
                { text: 'Sắp diễn ra', value: 'upcoming' },
                { text: 'Đã kết thúc', value: 'closed' },
            ],
            onFilter: (value, record) => record.status === value,
        },
        {
            title: 'Mô tả',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
        },
        {
            title: 'Thao tác',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <Button 
                        type="link" 
                        onClick={() => navigate(`/periods/edit/${record.id}`)}
                    >
                        Sửa
                    </Button>
                    <Button 
                        type="link" 
                        onClick={() => navigate(`/periods/${record.id}`)}
                    >
                        Chi tiết
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <Card 
            title="Danh sách đợt tuyển sinh" 
            extra={
                <Space>
                    <Input
                        placeholder="Tìm kiếm đợt tuyển sinh..."
                        prefix={<SearchOutlined />}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ width: 250 }}
                    />
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => navigate('/periods/create')}
                    >
                        Thêm đợt tuyển sinh
                    </Button>
                </Space>
            }
        >
            <Table
                columns={columns}
                dataSource={periods}
                rowKey="id"
                loading={loading}
                pagination={{
                    total: periods.length,
                    pageSize: 10,
                    showTotal: (total) => `Tổng số ${total} đợt tuyển sinh`,
                    showSizeChanger: true,
                    showQuickJumper: true,
                }}
            />
        </Card>
    );
};

export default PeriodList; 